import React, { useState } from 'react';
import { ClockCircleOutlined, BookOutlined, UserOutlined, CalendarOutlined, FlagOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PRIORITY, STATUS } from '../config/Config.js';
import { calculateRemainingTime } from '../services/remainingTimeService';
import { useUser } from '../contexts/UserContext';
import './TaskCard.css';

// Helper component for user avatar with error handling
const UserAvatar = ({ userInfo, size = 'normal' }) => {
    const [imgError, setImgError] = useState(false);

    const hasValidUrl = userInfo.url && userInfo.url.trim() !== '' && !imgError;
    const sizeClass = size === 'small' ? 'task-card-avatar-small' : size === 'xsmall' ? 'task-card-avatar-xsmall' : '';

    if (hasValidUrl) {
        return (
            <img
                src={userInfo.url}
                alt={userInfo.name}
                onError={() => setImgError(true)}
                className={sizeClass}
            />
        );
    }

    return (
        <div className={`task-card-avatar-placeholder ${sizeClass}`}>
            <UserOutlined />
        </div>
    );
};

// Get progress bar color based on percentage
const getProgressColor = (progress) => {
    if (progress >= 100) return '#22c55e'; // Green - completed
    if (progress >= 50) return '#eab308';  // Yellow - middle
    return '#ef4444'; // Red - start
};

const TaskCard = ({ task, onEdit, onDelete }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { user } = useUser();

    const taskData = task.task;
    const creator = task.creator;
    const categories = task.categories || [];
    const assignedUsers = task.assignedUsers || [];

    // Check if current user is the creator
    const isCreator = user.id === creator.id;

    // Status mapping
    const getStatusClass = (status) => {
        switch (status) {
            case 4: return 'completed';
            case 3: return 'in-progress';
            case 2: return 'pending';
            case 1: return 'postponed';
            default: return 'pending';
        }
    };

    // Calculate remaining time
    const getDeadlineInfo = () => {
        if (taskData.status === 1 || taskData.progress === 100) {
            return null;
        }
        return calculateRemainingTime(taskData.estimatedCompleteDate);
    };

    const deadlineInfo = getDeadlineInfo();

    // Get deadline class
    const getDeadlineClass = () => {
        if (!deadlineInfo) return 'normal';
        if (deadlineInfo.status.includes('-')) return 'overdue';
        const days = parseInt(deadlineInfo.status);
        if (days <= 3) return 'soon';
        return 'normal';
    };

    // Toggle expand
    const handleCardClick = () => {
        setIsExpanded(!isExpanded);
    };

    // Get first category for collapsed view
    const primaryCategory = categories[0];

    // Collapsed Card View
    if (!isExpanded) {
        return (
            <div className="task-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
                {/* Status Badge */}
                <div className={`task-card-status ${getStatusClass(taskData.status)}`}>
                    {STATUS[taskData.status]}
                </div>

                {/* Header */}
                <div className="task-card-header">
                    <h3 className="task-card-title">{taskData.title}</h3>
                    {taskData.description && (
                        <p className="task-card-description">{taskData.description}</p>
                    )}
                </div>

                {/* Meta Info */}
                <div className="task-card-meta">
                    <span className="task-card-date">
                        <CalendarOutlined /> {dayjs(taskData.estimatedCompleteDate).format('DD.MM.YYYY')}
                    </span>
                    {deadlineInfo && (
                        <span className={`task-card-deadline ${getDeadlineClass()}`}>
                            <ClockCircleOutlined /> {deadlineInfo.status}
                        </span>
                    )}
                    {primaryCategory && (
                        <span className="task-card-category">
                            <BookOutlined /> {primaryCategory.name}
                        </span>
                    )}
                    <span className="task-card-creator">
                        Oluşturan: <strong>{creator.name} {creator.surname}</strong>
                    </span>
                </div>

                {/* Footer */}
                <div className="task-card-footer">
                    <div className="task-card-info">
                        <div className="task-card-priority">
                            <span className="task-card-priority-indicator"></span>
                            {PRIORITY[taskData.priority]}
                        </div>

                    </div>

                    <div className="task-card-avatars">
                        {assignedUsers.length > 0 ? (
                            assignedUsers.slice(0, 4).map((assignedUser, index) => (
                                <div className="task-card-avatar" key={assignedUser.id || index}>
                                    <UserAvatar userInfo={assignedUser} />
                                </div>
                            ))
                        ) : (
                            <div className="task-card-avatar">
                                <div className="task-card-avatar-placeholder">
                                    <UserOutlined />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="task-card-progress">
                    <div className="task-card-progress-bar">
                        <div
                            className="task-card-progress-fill"
                            style={{
                                width: `${taskData.progress}%`,
                                background: getProgressColor(taskData.progress)
                            }}
                        />
                    </div>
                    <div className="task-card-progress-text">{taskData.progress}%</div>
                </div>
            </div>
        );
    }

    // Expanded Card View (Mobile App Style)
    return (
        <div className="task-card-expanded-wrapper" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
            {/* Dark Header Section */}
            <div className="task-card-expanded-header">
                <div className="task-card-expanded-header-content">
                    <h2 className="task-card-expanded-title">{taskData.title}</h2>
                    <div className={`task-card-status ${getStatusClass(taskData.status)}`}>
                        {STATUS[taskData.status]}
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="task-card-progress expanded">
                    <div className="task-card-progress-bar">
                        <div
                            className="task-card-progress-fill"
                            style={{
                                width: `${taskData.progress}%`,
                                background: getProgressColor(taskData.progress)
                            }}
                        />
                    </div>
                    <div className="task-card-progress-text light">{taskData.progress}%</div>
                </div>
            </div>

            {/* Açıklama Section */}
            {taskData.description && (
                <div className="task-card-section">
                    <h4 className="task-card-section-title">Açıklama</h4>
                    <p className="task-card-section-content">{taskData.description}</p>
                </div>
            )}

            {/* Detaylar Section */}
            <div className="task-card-section">
                <h4 className="task-card-section-title">Detaylar</h4>
                <div className="task-card-detail-list">
                    <div className="task-card-detail-item">
                        <span className="task-card-detail-label">
                            <FlagOutlined /> Öncelik:
                        </span>
                        <span className="task-card-detail-value priority">{PRIORITY[taskData.priority]}</span>
                    </div>
                    <div className="task-card-detail-item">
                        <span className="task-card-detail-label">
                            <BookOutlined /> Kategori:
                        </span>
                        <span className="task-card-detail-value">
                            {categories.length > 0 ? categories.map(c => c.name).join(', ') : '-'}
                        </span>
                    </div>
                    <div className="task-card-detail-item">
                        <span className="task-card-detail-label">
                            <UserOutlined /> Oluşturan:
                        </span>
                        <div className="task-card-detail-value creator">
                            <div className="task-card-creator-info">
                                <UserAvatar userInfo={creator} size="xsmall" />
                                <span className="task-card-creator-name">{creator.name} {creator.surname}</span>
                            </div>
                        </div>
                    </div>
                    <div className="task-card-detail-item">
                        <span className="task-card-detail-label">
                            <CalendarOutlined /> Tahmini Bitiş:
                        </span>
                        <span className="task-card-detail-value">
                            {dayjs(taskData.estimatedCompleteDate).format('DD MMMM YYYY')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Görevli Kişiler Section */}
            <div className="task-card-section">
                <h4 className="task-card-section-title">Görevli Kişiler</h4>
                <div className="task-card-users-chips">
                    {assignedUsers.map((u, idx) => (
                        <div className="task-card-user-chip" key={u.id || idx}>
                            <div className="task-card-user-chip-avatar">
                                <UserAvatar userInfo={u} size="small" />
                            </div>
                            <span>{u.name} {u.surname}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Action to Edit and Delete - Only for creator */}
            {isCreator && (
                <div className="task-card-section" style={{ display: 'flex', gap: '8px' }}>
                    <button className="task-card-edit-btn" onClick={(e) => { e.stopPropagation(); onEdit(task); }} style={{ flex: 1 }}>
                        Görevi Düzenle
                    </button>
                    {taskData.progress !== 100 && (
                        <button className="task-card-delete-btn" onClick={(e) => { e.stopPropagation(); onDelete(task); }}>
                            <DeleteOutlined />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default TaskCard;
